import React, { useEffect, useMemo, useState } from "react";
import { createMilestone, updateMilestone } from "../services/milestoneService";
import { X, Target, FileText, Calendar, Eye } from "lucide-react";
/**
 * MilestoneFormModal
 *
 * Create or edit a milestone.
 * (Sharing is intentionally disabled until backend exists)
 *
 * Props:
 * - open: boolean
 * - onClose: () => void
 * - projectId: string (required when creating)
 * - milestone?: { _id, title, summary }
 * - onSaved?: (milestone) => void
 */
export default function MilestoneFormModal({
  open,
  onClose,
  projectId,
  milestone,
  onSaved,
}) {
  const isEdit = Boolean(milestone?._id);

  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [targetDate, setTargetDate] = useState("");
  const [visibility, setVisibility] = useState("internal");
  /* -------------------------------------------------------------------------- */
  /* Init                                                                       */
  /* -------------------------------------------------------------------------- */
  useEffect(() => {
  if (!open) return;
  setTitle(milestone?.title || "");
  setSummary(milestone?.summary || "");
  setTargetDate(
    milestone?.targetDate
      ? milestone.targetDate.slice(0, 10)
      : ""
  );
  setVisibility(
    milestone?.visibility || "internal"
  );
  setError("");
  setSaving(false);
}, [open, milestone]);

  const disabled = useMemo(() => {
    if (!title.trim()) return true;
    if (!isEdit && !projectId) return true;
    return false;
  }, [title, isEdit, projectId]);

  /* -------------------------------------------------------------------------- */
  /* Save                                                                       */
  /* -------------------------------------------------------------------------- */
  const handleSave = async () => {
    if (disabled || saving) return;

    setSaving(true);
    setError("");

    try {
      let saved;

      if (isEdit) {
        saved = await updateMilestone(milestone._id, {
          title: title.trim(),
          summary: summary.trim() || undefined,
          targetDate: targetDate || null,
          visibility,
        });
      } else {
        saved = await createMilestone({
          projectId,
          title: title.trim(),
          summary: summary.trim() || undefined,
          targetDate: targetDate || null,
          visibility,
        });
      }

      onSaved?.(saved);
      onClose?.();
    } catch (e) {
      setError(
        e?.response?.data?.message ||
          e.message ||
          "Failed to save milestone"
      );
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  /* -------------------------------------------------------------------------- */
  /* Render                                                                     */
  /* -------------------------------------------------------------------------- */
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={() => !saving && onClose()}
      />

      {/* Modal */}
      <div className="relative w-full max-w-lg rounded-2xl bg-white border shadow-xl z-10">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-5 py-4">
          <h3 className="flex items-center gap-2 text-lg font-semibold">
            <Target className="w-5 h-5 text-indigo-600" />
            {isEdit ? "Edit Milestone" : "New Milestone"}
          </h3>
          <button
            onClick={onClose}
            disabled={saving}
            className="rounded-md p-1 text-gray-500 hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="mx-5 mt-3 rounded-lg border border-rose-200 bg-rose-50 p-2 text-xs text-rose-700">
            {error}
          </div>
        )}

        {/* Body */}
        <div className="p-5 space-y-4">
          {/* Title */}
          <div>
            <label className="text-sm font-medium flex items-center gap-2">
              <FileText className="w-4 h-4 text-gray-400" />
              Title
            </label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Phase 1 Complete"
              className="mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-200 outline-none"
              autoFocus
            />
          </div>

          {/* Summary */}
          <div>
            <label className="text-sm font-medium flex items-center gap-2">
              <FileText className="w-4 h-4 text-gray-400" />
              Summary
            </label>
            <textarea
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              rows={4}
              placeholder="Describe what this milestone represents (optional)"
              className="mt-1 w-full rounded-lg border px-3 py-2 text-sm resize-none focus:ring-2 focus:ring-indigo-200 outline-none"
            />
          </div>
        </div>
        <div>

        <label className="text-sm font-medium flex items-center gap-2">
          <Calendar className="w-4 h-4 text-gray-400" />
          Target Date
        </label>
        <input
          type="date"
          value={targetDate}
          onChange={(e) =>
            setTargetDate(e.target.value)
          }
          className="mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-200 outline-none"
        />
      </div>
        <div>
        <label className="text-sm font-medium flex items-center gap-2">
          <Eye className="w-4 h-4 text-gray-400" />
          Visibility
        </label>

        <select
          value={visibility}
          onChange={(e) =>
            setVisibility(e.target.value)
          }
          className="mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-200 outline-none"
        >
          <option value="internal">
            Internal Only
          </option>

          <option value="client">
            Shared with Client
          </option>
        </select>
      </div>
        {/* Footer */}
        <div className="flex justify-end gap-3 border-t bg-gray-50 px-5 py-4 rounded-b-2xl">
          <button
            onClick={onClose}
            disabled={saving}
            className="rounded-lg border px-4 py-2 text-sm hover:bg-gray-100"
          >
            Cancel
          </button>

          <button
            onClick={handleSave}
            disabled={saving || disabled}
            className="rounded-lg bg-gray-900 hover:bg-gray-800 px-4 py-2 text-sm text-white disabled:opacity-50"
          >
            {saving
              ? "Saving…"
              : isEdit
              ? "Save Changes"
              : "Create Milestone"}
          </button>
        </div>
      </div>
    </div>
  );
}